import { Instant } from "@js-joda/core";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import { DatasetType } from "api/model/enums";
import { v4 as uuidv4 } from "uuid";
import { IGanttRangeRelative } from "components/ComponentTypeTypes";
import { ChartData } from "components/dashboard/dataset-window/models/types";
import { I, T } from "services/lang/LanguageService";
import { TimeLoadConfig } from "models/dataset-models";
import { DweenTimeUnitEnum, getTimestamp } from "utils/time-utils";
import { enGB, lt, pl } from "date-fns/locale";
import * as XLSX from "xlsx";
import { DatasetLoadingConf, LineEditorLoadingTypes } from "components/dashboard/charts/interfaces";
import { ITableDataEntry } from "components/dashboard/product-template-table/utils/interfaces";
import { jwtDecode } from "jwt-decode";

const objectPropertiesToArray = (object: any) => {
  return Object.keys(object).map(key => {
    return {
      id: key,
      ...object[key],
    };
  });
};

const pad = (number: number) => {
  let r = String(number);
  if (r.length === 1) {
    r = `0${r}`;
  }
  return r;
};

const getBuildId = () => {
  return process.env.BUILD_ID;
};

const isProd = () => {
  return process.env.PRODUCTION === "true";
};

const isNumeric = (num: any) =>
  (typeof num === "number" || (typeof num === "string" && num.trim() !== "")) && !isNaN(num as number);

const timeConfigValidation = (config: TimeLoadConfig) => {
  const tempConfig: TimeLoadConfig = {
    alignStartTo: "HOURS" as DweenTimeUnitEnum,
    startOffsetUnits: "HOURS" as DweenTimeUnitEnum,
    startOffset: 0,
    lagUnits: "HOURS" as DweenTimeUnitEnum,
    lagOffset: 0,
    leadUnits: "HOURS" as DweenTimeUnitEnum,
    leadOffset: 0,
  };

  if (config.alignStartTo) {
    tempConfig.alignStartTo = config.alignStartTo;
  }

  if (config.startOffsetUnits) {
    tempConfig.startOffsetUnits = config.startOffsetUnits;
  }
  if (config.startOffset) {
    tempConfig.startOffset = config.startOffset;
  }

  if (config.lagUnits) {
    tempConfig.lagUnits = config.lagUnits;
  }
  if (config.lagOffset) {
    tempConfig.lagOffset = config.lagOffset;
  }

  if (config.leadUnits) {
    tempConfig.leadUnits = config.leadUnits;
  }
  if (config.leadOffset) {
    tempConfig.leadOffset = config.leadOffset;
  }

  return tempConfig;
};

const getInitials = (name: string) => {
  if (name !== "") {
    const hasTokens = name.includes(" ");
    const firstLetter = name[0].toUpperCase();
    const secondLetter = hasTokens ? name.charAt(name.lastIndexOf(" ") + 1).toUpperCase() : name.length > 1 ? name[1] : "";

    return firstLetter + secondLetter;
  }
  return "";
};

const getColorForString = (string: string): { color: string; bgColor: string } => {
  const colors = [
    "#55efc4",
    "#81ecec",
    "#74b9ff",
    "#a29bfe",
    "#dfe6e9",
    "#00b894",
    "#00cec9",
    "#0984e3",
    "#6c5ce7",
    "#b2bec3",
    "#ffeaa7",
    "#fab1a0",
    "#ff7675",
    "#fd79a8",
    "#fdcb6e",
    "#e17055",
    "#d63031",
    "#e84393",
  ];

  const hash = Math.abs(hashCode(string));
  const bucket = hash % colors.length;

  return { color: "#ffffff", bgColor: colors[bucket] };
};

const formatHexColor = (color: string) => {
  if (!color) return "#000000";
  return color.startsWith("#") ? color : `#${color}`;
};

const hashCode = (str: string) => str.split("").reduce((s, c) => (Math.imul(31, s) + c.charCodeAt(0)) | 0, 0);

const queryParam = (name: string) => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name);
};

type Join<K, P> = K extends string | number
  ? P extends string | number
    ? `${K}${"" extends P ? "" : "."}${P}`
    : never
  : never;

type Prev = [never, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, ...0[]];

export type Paths<T, D extends number = 6> = [D] extends [never]
  ? never
  : T extends object
    ? {
        [K in keyof T]-?: K extends string | number ? `${K}` | Join<K, Paths<T[K], Prev[D]>> : never;
      }[keyof T]
    : "";

export type Leaves<T, D extends number = 10> = [D] extends [never]
  ? never
  : T extends object
    ? { [K in keyof T]-?: Join<K, Leaves<T[K], Prev[D]>> }[keyof T]
    : "";

type DotPrefix<T extends string> = T extends "" ? "" : `.${T}`;

type DotNestedKeys<T> = (
  T extends object
    ? {
        [K in Exclude<keyof T, symbol>]: `${K}${DotPrefix<DotNestedKeys<T[K]>>}`;
      }[Exclude<keyof T, symbol>]
    : ""
) extends infer D
  ? Extract<D, string>
  : never;

const setObjAttribute = (obj: any, path: string, value: any) => {
  let i;
  const pathArr = path.split(".");
  for (i = 0; i < pathArr.length - 1; i++) obj = obj[pathArr[i]];

  obj[pathArr[i]] = value;
};

/**
 * Get attribute from object by path. If attribute is not found, or path is not valid, return default value
 * @param obj
 * @param path
 * @param rDefault
 */
const getObjAttribute = (obj: any, path: any, rDefault: any = null) => {
  if (!obj) {
    return rDefault;
  }

  let i;
  const pathArr = path.split(".");
  for (i = 0; i < pathArr.length - 1; i++) obj = obj[pathArr[i]];

  return obj[pathArr[i]] || rDefault;
};

const standardDeviation = (numArray: number[], averageCalculated?: number): number | undefined => {
  const arrLength = numArray.length;
  if (arrLength <= 1) {
    return undefined;
  }

  const mean = averageCalculated || numArray.reduce((s, n) => s + n) / arrLength;
  const variance = numArray.reduce((s, n) => s + (n - mean) ** 2, 0) / (arrLength - 1);
  return Math.sqrt(variance);
};

const checkIfArraysLengthIsSame = (...arrays: Array<any>) => {
  const definedArrays = arrays.filter(arr => arr !== undefined);

  if (definedArrays.length === 0) {
    return true; // All arrays are undefined, so they are considered to have the same length
  }

  const firstArrayLength = definedArrays[0].length;

  return definedArrays.every(arr => arr.length === firstArrayLength);
};

const getChartSizeValue = (value: string | number | undefined): number => {
  if (!value) return 0;
  if (typeof value === "string") {
    const parsedNumber = parseFloat(value);
    return isNaN(parsedNumber) ? 400 : parsedNumber;
  }
  return value;
};

const isJSON = (str: string | undefined) => {
  try {
    if (typeof str === "string") {
      JSON.parse(str);
      return true;
    }
    return false;
  } catch (e) {
    return false;
  }
};

const isParametersValid = (parametersData: any, selectedParameters: any) => {
  const message = { isValid: true, details: "" };

  if (!parametersData.parameters || parametersData.parameters.length === 0) {
    message.isValid = false;
    message.details = T("viking-malt.validation.need-at-least-one-parameter");
  } else if (selectedParameters.length > parametersData.parameters.length) {
    message.isValid = false;
    message.details = T("viking-malt.validation.all-parameters-need-sp-or-min-max-set-for-them");
  } else if (
    parametersData.parameters.some(
      (parameter: any) => parameter.sp === null && parameter.min === null && parameter.max === null
    )
  ) {
    message.isValid = false;
    message.details = T("viking-malt.validation.all-parameters-need-sp-or-min-max-set-for-them");
  }

  return message;
};

const findValue = (arr: Array<any>, targetTime: number) => {
  let left = 0;
  let right = arr.length - 1;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const tempDate = new Date(arr[mid].key);
    tempDate.setMilliseconds(0);

    if (tempDate.getTime() === targetTime) {
      return arr[mid].value;
    }
    if (tempDate.getTime() < targetTime) {
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }

  return null;
};

const isDatasetIdentifier = (value: string) => {
  const idRegex = /^(\d+):([A-Z_]+)$/;
  if (!idRegex.test(value)) return false;

  const parts = value.split(":");
  if (parts.length !== 2) return false;

  const datasetType = parts[1] as keyof typeof DatasetType;
  if (DatasetType[datasetType] === undefined) return false;

  return true;
};

const isObjectEmpty = (obj: any) => {
  return Object.keys(obj).length === 0;
};

const addLeadingZeros = (num: number, targetLength: number) => {
  return String(num).padStart(targetLength, "0");
};
const handleRowReorder = <T = any>(
  data: Array<Object> | undefined,
  configSetter: (config: T) => void,
  currentConfig: T = null as any,
  property: keyof T = "datasets" as any,
  dragIndex: number,
  dropIndex: number
) => {
  const orderedValues = [...(data || [])];
  const movedValue = orderedValues[dragIndex];
  orderedValues.splice(dragIndex, 1);
  orderedValues.splice(dropIndex, 0, movedValue);
  currentConfig ? configSetter({ ...currentConfig, [property]: orderedValues }) : configSetter(orderedValues as any);
};

const handleRowReorderEvt =
  <T = any>(
    data: Array<any> | undefined,
    configSetter: (config: T) => void,
    currentConfig: T = null as any,
    property?: keyof T
  ) =>
  (event: any) => {
    handleRowReorder(data, configSetter, currentConfig, property, event.dragIndex, event.dropIndex);
  };

const getHoverTemplateValue = (data: ChartData | DatasetDataInfoResponse) => {
  const hoverTemplateValue = data.stringValueMapper ? "%{customdata}" : "%{y:.3f}";
  return `<b>${hoverTemplateValue}</b> at %{x} <br>${I(data.title)}<extra></extra>`;
};

const normalizeString = (value: string) => {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

const isDatasetLoadingConf = (loadConf?: any): loadConf is DatasetLoadingConf => {
  return loadConf?.type === LineEditorLoadingTypes.TIME;
};

const findObjectVal = <T>(object: T, key: string): unknown => {
  if (key && key.includes(".")) {
    const [key1, ...restKeys] = key.split(".");
    if (!object[key1 as keyof T]) return null;
    return findObjectVal(object[key1 as keyof T], restKeys.join("."));
  }
  return object[key as keyof T];
};

const exportExcel = (data: any[], tableName: string) => {
  const wb = XLSX.utils.book_new();
  const headersTitles = Object.keys(data[0] || {});

  const wsData = [
    headersTitles,
    ...data.map(row =>
      headersTitles.map(header => {
        const value = row[header];

        if (value instanceof Date) {
          return value.toISOString().replace("T", " ").split(".")[0];
        }
        if ([T("word.device"), T("mdm.serial-no"), T("mdm.device-serial-no")].includes(header)) {
          return value;
        }
        if (typeof value === "string" && !Number.isNaN(Number(value))) {
          return parseFloat(value);
        }
        return value !== undefined && value !== null ? value : "-";
      })
    ),
  ];

  const ws = XLSX.utils.aoa_to_sheet(wsData);
  ws["!cols"] = headersTitles.map(() => ({ width: 20 }));

  XLSX.utils.book_append_sheet(wb, ws, "Data");
  XLSX.writeFile(wb, `${tableName}-${new Date().toISOString().split("T")[0]}.xlsx`);
};

const getMinMax = (arr: number[]) => {
  let min = Infinity;
  let max = -Infinity;

  for (let i = 0; i < arr.length; i++) {
    if (arr[i] < min) min = arr[i];
    if (arr[i] > max) max = arr[i];
  }

  return { min, max };
};

const hasDecimals = (num: number | undefined) => {
  if (!num || typeof num !== "number") return false;
  return num % 1 !== 0;
};

const moveItem = <T>(array: T[], fromIndex: number, toIndex: number) => {
  if (fromIndex < 0 || toIndex < 0 || fromIndex >= array.length || toIndex >= array.length) {
    return array;
  }

  const item = array[fromIndex];
  const newArray = array.toSpliced(fromIndex, 1);
  newArray.splice(toIndex, 0, item);
  return newArray;
};

const rangeBody = (range: number[] | undefined): string => {
  const isRangeValid = range && range.every(value => value !== undefined && value !== null);

  return isRangeValid ? `[ ${range[0]} : ${range[1]} ]` : T("word.no-validity-range");
};

const isNonOverlapping = (s: number | null, eEnd: number | null, exStart: number | null, exEnd: number | null): boolean => {
  return eEnd == null || (exStart != null && exEnd != null && eEnd <= exStart) || (s != null && exEnd != null && s >= exEnd);
};

const assignOverlapTrackIndex = (entry: ITableDataEntry, all: ITableDataEntry[], gr: IGanttRangeRelative): number => {
  const getTimeRange = (e: ITableDataEntry) => {
    return {
      start: getTimestamp(e.entries[gr.startDateColumn!]?.value),
      end: getTimestamp(e.entries[gr.endDateColumn!]?.value),
    };
  };

  const placedTracks: ITableDataEntry[][] = [];

  all.forEach(candidate => {
    const { start: s, end: eEnd } = getTimeRange(candidate);

    const trackIndex = placedTracks.findIndex(track =>
      track.every(existing => {
        const { start: exStart, end: exEnd } = getTimeRange(existing);
        return isNonOverlapping(s, eEnd, exStart, exEnd);
      })
    );

    if (trackIndex !== -1) {
      placedTracks[trackIndex].push(candidate);
    } else {
      placedTracks.push([candidate]);
    }
  });

  const foundIndex = placedTracks.findIndex(track => track.includes(entry));
  return foundIndex !== -1 ? foundIndex : placedTracks.length;
};

const rerenders = new Map<string, number>();

export function logRender(thing: any) {
  let name: string;
  if (thing && thing.name) {
    name = thing.name;
  } else {
    name = thing;
  }
  const rends = rerenders.get(name) || 0;
  rerenders.set(name, rends + 1);
  console.log(`Rendered [${name}][${rends}] at ${Instant.now().toString()} `);
}

const getTokenStaleTime = (token: string): number => {
  try {
    const decoded = jwtDecode<{ exp: number }>(token);
    const now = Date.now();
    const expiry = decoded.exp * 1000;
    const leeway = 30000;
    const timeUntilExpiry = expiry - now - leeway;

    return timeUntilExpiry;
  } catch {
    return 0;
  }
};

const roundIfDecimal = (value: unknown, precision = 2) => {
  if (typeof value !== "number" || Number.isNaN(Number(value))) return value;
  if (value % 1 !== 0) return value.toFixed(precision);
  return value;
};

const getLocale = (userLang?: string) => {
  switch (userLang) {
    case "lt":
      return lt;
    case "pl":
      return pl;
    default:
      return enGB;
  }
};
function ensureKeysWithUUID<T extends Record<string, any>, K extends keyof T>(key: K, arr?: T[]) {
  if (!arr) return [];

  return arr.map(item => ({
    ...item,
    [key]: item[key] ?? uuidv4(),
  }));
}

export {
  addLeadingZeros,
  checkIfArraysLengthIsSame,
  exportExcel,
  findObjectVal,
  findValue,
  getBuildId,
  getChartSizeValue,
  getColorForString,
  getHoverTemplateValue,
  getInitials,
  getObjAttribute,
  handleRowReorderEvt,
  isDatasetIdentifier,
  isJSON,
  isNumeric,
  isObjectEmpty,
  isParametersValid,
  isProd,
  normalizeString,
  objectPropertiesToArray,
  pad,
  formatHexColor,
  queryParam,
  setObjAttribute,
  standardDeviation,
  timeConfigValidation,
  getMinMax,
  hasDecimals,
  moveItem,
  rangeBody,
  isDatasetLoadingConf,
  isNonOverlapping,
  assignOverlapTrackIndex,
  getTokenStaleTime,
  roundIfDecimal,
  getLocale,
  ensureKeysWithUUID,
};
